// components/ConvertWonLeadModal.tsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  X,
  Search,
  Plus,
  Minus,
  Trash2,
  CheckCircle2,
  Loader2,
  DollarSign,
  Package,
  ShoppingBag,
  Percent,
  Check,
  Tag,
} from 'lucide-react';
import { Lead, leadsApi, api } from '../services/api';
import toast from 'react-hot-toast';

interface ConvertWonLeadModalProps {
  isOpen: boolean;
  lead: Lead | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface CartItem {
  id: string;
  item: any;
  name: string;
  itemType: 'Product' | 'Service' | 'Combo';
  quantity: number;
  unitPrice: number;
}

const PAYMENT_METHODS = [
  { value: 'POS', label: 'POS / Cartão' },
  { value: 'Cash', label: 'Dinheiro (Espécie)' },
  { value: 'M-Pesa', label: 'M-Pesa' },
  { value: 'E-Mola', label: 'E-Mola' },
  { value: 'Transferência', label: 'Transferência Bancária' },
];

export const ConvertWonLeadModal: React.FC<ConvertWonLeadModalProps> = ({
  isOpen,
  lead,
  onClose,
  onSuccess,
}) => {
  const [catalog, setCatalog] = useState<any[]>([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedItems, setSelectedItems] = useState<CartItem[]>([]);

  // Financeiro
  const [discountType, setDiscountType] = useState<'fixed' | 'percentage'>('fixed');
  const [discountValue, setDiscountValue] = useState<number>(0);
  const [taxIncluded, setTaxIncluded] = useState<boolean>(true);
  const [taxRate, setTaxRate] = useState<number>(16); // 16% IVA padrão MZ
  const [paymentMethod, setPaymentMethod] = useState<string>('POS');
  const [confirmedTotal, setConfirmedTotal] = useState<string>('');
  const [isManualTotalEdited, setIsManualTotalEdited] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Carregar catálogo ao abrir o modal
  useEffect(() => {
    if (!isOpen) return;

    const loadCatalog = async () => {
      setLoadingCatalog(true);
      try {
        const [products, bundles, services] = await Promise.all([
          api.products.getAll(),
          api.bundles.getAll(),
          api.services.getAll(),
        ]);
        const all = [
          ...products.map((p: any) => ({ ...p, _type: 'Product' })),
          ...bundles.map((b: any) => ({ ...b, _type: 'Combo' })),
          ...services.map((s: any) => ({ ...s, _type: 'Service' })),
        ];
        setCatalog(all);
      } catch (err) {
        console.error('Erro ao carregar catálogo para fecho de venda:', err);
        toast.error('Não foi possível carregar o catálogo de produtos/serviços.');
      } finally {
        setLoadingCatalog(false);
      }
    };

    loadCatalog();
  }, [isOpen]);

  // Reset do estado quando o lead muda
  useEffect(() => {
    if (lead) {
      setSelectedItems([]);
      setDiscountValue(0);
      setTaxIncluded(true);
      setIsManualTotalEdited(false);
      if (lead.dealValue && lead.dealValue > 0) {
        setConfirmedTotal(lead.dealValue.toString());
      } else {
        setConfirmedTotal('');
      }
    }
  }, [lead]);

  // Catálogo filtrado por pequisa
  const filteredCatalog = useMemo(() => {
    if (!search.trim()) return catalog;
    const q = search.toLowerCase().trim();
    return catalog.filter(
      (item) =>
        item.name?.toLowerCase().includes(q) ||
        item.category?.toLowerCase().includes(q) ||
        item.sku?.toLowerCase().includes(q)
    );
  }, [catalog, search]);

  // Cálculos financeiros
  const subtotal = useMemo(() => {
    return selectedItems.reduce((sum, ci) => sum + ci.quantity * ci.unitPrice, 0);
  }, [selectedItems]);

  const discountAmount = useMemo(() => {
    if (discountValue <= 0) return 0;
    if (discountType === 'percentage') {
      return (subtotal * Math.min(100, discountValue)) / 100;
    }
    return Math.min(subtotal, discountValue);
  }, [subtotal, discountValue, discountType]);

  const subtotalAfterDiscount = Math.max(0, subtotal - discountAmount);

  const taxAmount = useMemo(() => {
    if (taxIncluded) return 0;
    return (subtotalAfterDiscount * taxRate) / 100;
  }, [subtotalAfterDiscount, taxIncluded, taxRate]);

  const calculatedTotal = subtotalAfterDiscount + taxAmount;

  // Atualiza o total confirmado automaticamente a menos que o utilizador edite manualmente
  useEffect(() => {
    if (!isManualTotalEdited) {
      setConfirmedTotal(calculatedTotal > 0 ? calculatedTotal.toString() : '');
    }
  }, [calculatedTotal, isManualTotalEdited]);

  if (!isOpen || !lead) return null;

  // Adicionar item ao carrinho
  const handleAddItem = (item: any) => {
    const itemType: 'Product' | 'Service' | 'Combo' =
      item._type || (item.type === 'Combo' || item.type === 'Subscription' ? 'Combo' : 'stockQuantity' in item ? 'Product' : 'Service');

    let price = item.basePrice ?? item.price ?? item.billingPricePerCycle ?? 0;

    setSelectedItems((prev) => {
      const existing = prev.find((ci) => ci.id === item._id);
      if (existing) {
        return prev.map((ci) =>
          ci.id === item._id ? { ...ci, quantity: ci.quantity + 1 } : ci
        );
      }
      return [
        ...prev,
        {
          id: item._id,
          item,
          name: item.name,
          itemType,
          quantity: 1,
          unitPrice: Number(price) || 0,
        },
      ];
    });
  };

  const handleQuantityChange = (id: string, delta: number) => {
    setSelectedItems((prev) =>
      prev
        .map((ci) => {
          if (ci.id === id) {
            const newQty = ci.quantity + delta;
            return newQty > 0 ? { ...ci, quantity: newQty } : null;
          }
          return ci;
        })
        .filter(Boolean) as CartItem[]
    );
  };

  const handleUnitPriceChange = (id: string, newPrice: number) => {
    setSelectedItems((prev) =>
      prev.map((ci) => (ci.id === id ? { ...ci, unitPrice: newPrice } : ci))
    );
  };

  const handleRemoveItem = (id: string) => {
    setSelectedItems((prev) => prev.filter((ci) => ci.id !== id));
  };

  // Guardar Venda & Converter Lead
  const handleConfirmWonAndSale = async () => {
    setSubmitting(true);
    try {
      // 1. Converter Lead para Cliente no backend
      const convertRes = await leadsApi.convert(lead._id!);
      const createdClient = convertRes?.client;

      const finalTotalNumber = parseFloat(confirmedTotal) || calculatedTotal;

      // 2. Registar Venda no histórico se houver itens ou valor total > 0
      if (selectedItems.length > 0 || finalTotalNumber > 0) {
        const saleItems = selectedItems.map((ci) => ({
          productId: ci.item._id,
          name: ci.name,
          quantity: ci.quantity,
          priceAtSale: ci.unitPrice,
          itemType: ci.itemType === 'Combo' ? 'Combo' : ci.itemType === 'Product' ? 'Product' : 'Service',
        }));

        const salePayload: any = {
          items: saleItems.length > 0 ? saleItems : [
            {
              productId: lead._id,
              name: `Conversão de Lead: ${lead.name}`,
              quantity: 1,
              priceAtSale: finalTotalNumber,
              itemType: 'Service',
            }
          ],
          status: 'Pago 100%',
          subtotal,
          total: finalTotalNumber,
          amountPaid: finalTotalNumber,
          paymentMethod,
          customer: {
            name: lead.name,
            phone: lead.phone || '',
          },
          clientId: createdClient?._id,
          notes: `Venda gerada automaticamente na conversão do Lead: ${lead.name}`,
          dueDate: new Date().toISOString().split('T')[0],
        };

        if (discountValue > 0) {
          salePayload.discount = {
            code: 'FECHO_LEAD',
            type: discountType === 'percentage' ? 'Percentage' : 'Fixed',
            value: discountValue,
            amount: discountAmount,
          };
        }

        await api.sales.create(salePayload);
      }

      toast.success(`Lead "${lead.name}" convertido com sucesso e venda de MT ${finalTotalNumber.toLocaleString()} registada!`);
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Erro ao converter lead e registar venda:', err);
      toast.error(err.message || 'Erro ao processar a conversão e registar a venda.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[120] p-3 sm:p-6 overflow-y-auto backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-full max-w-4xl shadow-2xl flex flex-col max-h-[92vh] border border-emerald-100 animate-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 px-6 py-4 text-white flex items-center justify-between shrink-0 rounded-t-2xl">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <CheckCircle2 size={24} className="text-emerald-200" /> Registar Venda & Converter Lead
            </h2>
            <p className="text-xs text-emerald-100 mt-0.5">
              Lead: <strong>{lead.name}</strong> • {lead.phone || lead.email || 'Sem contacto'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
          
          {/* Coluna Esquerda (8/12): Pesquisa & Seleção do Catálogo */}
          <div className="lg:col-span-7 flex flex-col gap-4 border-b lg:border-b-0 lg:border-r border-slate-200 pb-4 lg:pb-0 lg:pr-6">
            <h3 className="font-bold text-slate-800 text-sm flex items-center gap-2">
              <ShoppingBag size={18} className="text-emerald-600" /> Catálogo (Produtos, Serviços & Combos)
            </h3>

            {/* Pesquisa */}
            <div className="relative">
              <Search className="absolute left-3.5 top-2.5 text-slate-400" size={16} />
              <input
                type="text"
                placeholder="Pesquisar por nome, categoria ou código..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-xs rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-emerald-500 focus:outline-none transition"
              />
            </div>

            {/* Lista do Catálogo */}
            {loadingCatalog ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="animate-spin text-emerald-600" size={28} />
              </div>
            ) : filteredCatalog.length === 0 ? (
              <div className="text-center py-10 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                <Package className="mx-auto text-slate-300 mb-2" size={36} />
                <p className="text-xs text-slate-500 font-medium">Nenhum item encontrado no catálogo.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[350px] overflow-y-auto pr-1">
                {filteredCatalog.map((item) => {
                  const price = item.basePrice ?? item.price ?? item.billingPricePerCycle ?? 0;
                  const isSelected = selectedItems.some((ci) => ci.id === item._id);

                  return (
                    <div
                      key={item._id}
                      onClick={() => handleAddItem(item)}
                      className={`p-3 rounded-xl border text-left cursor-pointer transition flex flex-col justify-between ${
                        isSelected
                          ? 'border-emerald-500 bg-emerald-50/60 ring-1 ring-emerald-500'
                          : 'border-slate-200 bg-white hover:border-emerald-300 hover:shadow-sm'
                      }`}
                    >
                      <div>
                        <div className="flex items-center justify-between gap-1 mb-1">
                          <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                            {item._type === 'Combo' ? 'Combo' : item._type === 'Service' ? 'Serviço' : 'Produto'}
                          </span>
                          {isSelected && <Check size={14} className="text-emerald-600 shrink-0" />}
                        </div>
                        <h4 className="font-semibold text-slate-800 text-xs line-clamp-1">{item.name}</h4>
                      </div>
                      <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between">
                        <span className="text-xs font-bold text-emerald-700">
                          {Number(price).toLocaleString()} MT
                        </span>
                        <button
                          type="button"
                          className="text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 bg-indigo-50 px-2 py-0.5 rounded-md"
                        >
                          + Adicionar
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Coluna Direita (5/12): Resumo do Pedido & Fecho Financeiro */}
          <div className="lg:col-span-5 flex flex-col gap-4">
            <h3 className="font-bold text-slate-800 text-sm flex items-center gap-2">
              <Tag size={18} className="text-emerald-600" /> Resumo do Pedido
            </h3>

            {/* Lista do Carrinho Selecionado */}
            <div className="space-y-2 max-h-[200px] overflow-y-auto pr-1">
              {selectedItems.length === 0 ? (
                <div className="p-4 text-center bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-400">
                  Nenhum item selecionado. Clique nos produtos/serviços ao lado para adicionar.
                </div>
              ) : (
                selectedItems.map((ci) => (
                  <div key={ci.id} className="p-2.5 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-slate-800 truncate">{ci.name}</p>
                      <div className="flex items-center gap-1 mt-1">
                        <input
                          type="number"
                          value={ci.unitPrice}
                          onChange={(e) => handleUnitPriceChange(ci.id, parseFloat(e.target.value) || 0)}
                          className="w-20 rounded border border-slate-300 px-1.5 py-0.5 text-xs font-medium"
                          title="Preço unitário negociado"
                        />
                        <span className="text-[10px] text-slate-400">MT/un.</span>
                      </div>
                    </div>

                    {/* Qtd Controls */}
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleQuantityChange(ci.id, -1)}
                        className="p-1 rounded bg-slate-200 text-slate-700 hover:bg-slate-300"
                      >
                        <Minus size={12} />
                      </button>
                      <span className="text-xs font-bold w-5 text-center">{ci.quantity}</span>
                      <button
                        onClick={() => handleQuantityChange(ci.id, 1)}
                        className="p-1 rounded bg-slate-200 text-slate-700 hover:bg-slate-300"
                      >
                        <Plus size={12} />
                      </button>
                      <button
                        onClick={() => handleRemoveItem(ci.id)}
                        className="p-1 text-red-500 hover:text-red-700 ml-1"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Ajustes Financeiros (Desconto & IVA) */}
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
              {/* Desconto */}
              <div className="grid grid-cols-12 gap-2 items-center">
                <label className="col-span-5 text-xs font-semibold text-slate-600">Desconto:</label>
                <div className="col-span-7 flex items-center gap-1">
                  <input
                    type="number"
                    min="0"
                    value={discountValue || ''}
                    onChange={(e) => setDiscountValue(parseFloat(e.target.value) || 0)}
                    placeholder="0"
                    className="w-full rounded-lg border border-slate-300 px-2 py-1 text-xs"
                  />
                  <select
                    value={discountType}
                    onChange={(e) => setDiscountType(e.target.value as any)}
                    className="rounded-lg border border-slate-300 px-2 py-1 text-xs bg-white"
                  >
                    <option value="fixed">MT</option>
                    <option value="percentage">%</option>
                  </select>
                </div>
              </div>

              {/* Toggle IVA */}
              <div className="flex items-center justify-between pt-2 border-t border-slate-200/60">
                <label className="flex items-center gap-2 text-xs font-medium text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={taxIncluded}
                    onChange={(e) => setTaxIncluded(e.target.checked)}
                    className="rounded text-emerald-600 h-4 w-4"
                  />
                  Preço com IVA Incluso
                </label>
                {!taxIncluded && (
                  <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded">
                    +{taxRate}% IVA
                  </span>
                )}
              </div>

              {/* Método de Pagamento */}
              <div className="pt-2 border-t border-slate-200/60">
                <label className="block text-xs font-semibold text-slate-600 mb-1">Método de Pagamento</label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs bg-white font-medium text-slate-800"
                >
                  {PAYMENT_METHODS.map((pm) => (
                    <option key={pm.value} value={pm.value}>{pm.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Total Final Confirmado */}
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl space-y-1.5">
              <div className="flex items-center justify-between text-xs text-slate-600">
                <span>Subtotal Itens:</span>
                <span className="font-semibold">{subtotal.toLocaleString()} MT</span>
              </div>
              {discountAmount > 0 && (
                <div className="flex items-center justify-between text-xs text-emerald-700 font-medium">
                  <span>Desconto Aplicado:</span>
                  <span>- {discountAmount.toLocaleString()} MT</span>
                </div>
              )}

              <div className="pt-2 border-t border-emerald-200/80">
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Valor Total Confirmado (MT)
                </label>
                <input
                  type="number"
                  value={confirmedTotal}
                  onChange={(e) => {
                    setConfirmedTotal(e.target.value);
                    setIsManualTotalEdited(true);
                  }}
                  placeholder="0.00"
                  className="w-full rounded-xl border border-emerald-300 bg-white px-3 py-2 text-base font-bold text-emerald-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 shadow-inner"
                />
                <p className="text-[10px] text-slate-500 mt-1">
                  Pode confirmar ou ajustar o valor final acordado com o cliente.
                </p>
              </div>
            </div>

            {/* Botões de Ação */}
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="py-2.5 px-4 rounded-xl border border-slate-300 text-slate-700 text-xs font-semibold hover:bg-slate-100 transition"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmWonAndSale}
                disabled={submitting}
                className="flex-1 py-2.5 px-4 rounded-xl text-white font-bold text-xs flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 shadow-lg shadow-emerald-200 transition"
              >
                {submitting ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <>
                    <CheckCircle2 size={16} /> Confirmar & Registar Venda
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
